import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { LOGIN_URL, MICROSOFT_AUTH_URL, REGISTER_URL } from "../services/api";
import { resolveTenantId } from "../utils/tenant";
import { normalizeProductAccess } from "../constants/products";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);

  const persistAuthSession = ({
    token: nextToken,
    user: nextUser,
    company,
  }) => {
    const normalizedUser = {
      ...nextUser,
      productAccess: normalizeProductAccess(nextUser?.productAccess),
    };

    localStorage.setItem("token", nextToken);
    localStorage.setItem("user", JSON.stringify(normalizedUser));
    if (company) {
      localStorage.setItem("company", JSON.stringify(company));
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${nextToken}`;

    setToken(nextToken);
    setUser(normalizedUser);
  };

  // Load user from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    console.log({ storedToken });
    const storedUser = localStorage.getItem("user");
    console.log({ storedUser });

    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        const normalizedUser = {
          ...parsedUser,
          productAccess: normalizeProductAccess(parsedUser?.productAccess),
        };
        setToken(storedToken);
        setUser(normalizedUser);
        localStorage.setItem("user", JSON.stringify(normalizedUser));
        axios.defaults.headers.common["Authorization"] =
          `Bearer ${storedToken}`;
      } catch (error) {
        console.error("Error parsing stored user:", error);
        localStorage.removeItem("token");
        localStorage.removeItem("user");
      }
    }
    setIsLoading(false);
  }, []);

  // Register function
  const register = async (userData) => {
    try {
      const response = await axios.post(REGISTER_URL, userData);

      const { data } = response.data;
      const newToken = data.token;
      const newUser = data.user;
      const company = data.company;

      persistAuthSession({ token: newToken, user: newUser, company });

      return { success: true, user: newUser, company };
    } catch (err) {
      console.error("Registration error:", err);
      return {
        success: false,
        message: err.response?.data?.message || "Registration failed",
      };
    }
  };

  // Login function
  const login = async ({ email, password, companySlug }) => {
    try {
      let resolvedCompanySlug = companySlug || resolveTenantId();
      if (resolvedCompanySlug === "mis") {
        resolvedCompanySlug = "iom";
      }
      const adjustedRequestBody = {
        email,
        password,
        ...(resolvedCompanySlug ? { companySlug: resolvedCompanySlug } : {}),
      };
      console.log(
        "Adjusted Request Body:",
        JSON.stringify(adjustedRequestBody, null, 2),
      );
      const response = await axios.post(LOGIN_URL, adjustedRequestBody);

      // Handle multiple companies scenario
      if (response.data.requiresCompanySelection) {
        return {
          success: false,
          requiresCompanySelection: true,
          companies: response.data.companies,
          message: response.data.message,
        };
      }

      const { data } = response.data;
      const newToken = data.token;
      const newUser = data.user;
      const company = data.company;
      persistAuthSession({ token: newToken, user: newUser, company });
      window.dispatchEvent(new Event("login"));
      return { success: true, user: newUser, company };
    } catch (err) {
      console.error("Login error:", err);

      // Check if it's a company selection response
      if (err.response?.data?.requiresCompanySelection) {
        return {
          success: false,
          requiresCompanySelection: true,
          companies: err.response.data.companies,
          message: err.response.data.message,
        };
      }

      return {
        success: false,
        message: err.response?.data?.message || "Login failed",
      };
    }
  };

  const microsoftAuth = async ({ accessToken, mode, companyName }) => {
    try {
      const companySlug = resolveTenantId();
      const response = await axios.post(MICROSOFT_AUTH_URL, {
        accessToken,
        mode,
        companyName,
        ...(companySlug ? { companySlug } : {}),
      });

      const { data } = response.data;
      const newToken = data.token;
      const newUser = data.user;
      const company = data.company;

      persistAuthSession({ token: newToken, user: newUser, company });
      window.dispatchEvent(new Event("login"));

      return { success: true, user: newUser, company };
    } catch (err) {
      console.error("Microsoft auth error:", err);
      return {
        success: false,
        message:
          err.response?.data?.message || "Microsoft authentication failed",
      };
    }
  };

  // Logout function
  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("company");
    delete axios.defaults.headers.common["Authorization"];
    setToken(null);
    setUser(null);
  };

  const products = normalizeProductAccess(user?.productAccess);
  const hasProductAccess = (product) => products.includes(product);

  const value = {
    user,
    products,
    token,
    isLoading,
    register,
    login,
    microsoftAuth,
    logout,
    hasProductAccess,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
